import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  XMarkIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { complaintApi, extractFieldErrors } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import {
  PageHeader,
  StatusBadge,
  DataTable,
  StatCard,
} from '../components/ui';
import type { Column, BadgeVariant } from '../components/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'NEW', label: 'New' },
  { key: 'UNDER_INVESTIGATION', label: 'Under Investigation' },
  { key: 'PENDING_RESPONSE', label: 'Pending Response' },
  { key: 'ESCALATED', label: 'Escalated' },
  { key: 'RESOLVED', label: 'Resolved' },
];

const STATUS_BADGE_MAP: Record<string, BadgeVariant> = {
  NEW: 'info',
  UNDER_INVESTIGATION: 'warning',
  PENDING_RESPONSE: 'warning',
  RESOLVED: 'success',
  ESCALATED: 'danger',
  CLOSED: 'neutral',
};

const PRIORITY_BADGE_MAP: Record<string, BadgeVariant> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
};

const CATEGORIES = [
  { value: 'BILLING', label: 'Billing' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'DISCRIMINATION', label: 'Discrimination' },
  { value: 'PRIVACY_COMPLAINT', label: 'Privacy' },
  { value: 'UNAUTHORIZED', label: 'Unauthorized' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'MARKETING_COMPLAINT', label: 'Marketing' },
  { value: 'OTHER_COMPLAINT', label: 'Other' },
];

const CHANNELS = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'EMAIL_CHANNEL', label: 'Email' },
  { value: 'MAIL', label: 'Mail' },
  { value: 'IN_PERSON', label: 'In Person' },
  { value: 'SOCIAL_MEDIA', label: 'Social Media' },
  { value: 'REGULATORY', label: 'Regulatory' },
  { value: 'ONLINE_FORM', label: 'Online Form' },
  { value: 'OTHER_CHANNEL', label: 'Other' },
];

const PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const STATUS_LABEL: Record<string, string> = {
  NEW: 'New',
  UNDER_INVESTIGATION: 'Under Investigation',
  PENDING_RESPONSE: 'Pending Response',
  RESOLVED: 'Resolved',
  ESCALATED: 'Escalated',
  CLOSED: 'Closed',
};

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label]),
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ComplaintManagementPage() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateDispute, setShowCreateDispute] = useState(false);
  const [disputeForm, setDisputeForm] = useState({
    customerName: '',
    subject: '',
    description: '',
    disputeType: 'REG_E',
    category: 'UNAUTHORIZED',
    priority: 'HIGH',
    channel: 'OTHER_CHANNEL',
  });

  const createDisputeMutation = useMutation({
    mutationFn: (data: any) => complaintApi.createDispute(teamId!, data),
    onSuccess: () => {
      toast.success('Dispute created with SLA deadlines');
      setShowCreateDispute(false);
      setDisputeForm({ customerName: '', subject: '', description: '', disputeType: 'REG_E', category: 'UNAUTHORIZED', priority: 'HIGH', channel: 'OTHER_CHANNEL' });
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      queryClient.invalidateQueries({ queryKey: ['complaintDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['complaintSlaDashboard'] });
    },
    onError: () => toast.error('Failed to create dispute'),
  });

  const statusFilter = STATUS_TABS[activeTab].key;

  // Dashboard stats
  const { data: dashData } = useQuery({
    queryKey: ['complaintDashboard', teamId],
    queryFn: () => complaintApi.dashboard(teamId!),
    enabled: !!teamId,
  });
  const dash = dashData?.data;

  // SLA dashboard
  const { data: slaData } = useQuery({
    queryKey: ['complaintSlaDashboard', teamId],
    queryFn: () => complaintApi.slaDashboard(teamId!),
    enabled: !!teamId,
  });
  const sla = slaData?.data;

  // Complaints list
  const { data: listData, isLoading } = useQuery({
    queryKey: ['complaints', teamId, statusFilter],
    queryFn: () =>
      complaintApi.list(teamId!, {
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      }),
    enabled: !!teamId,
  });
  const complaints = listData?.data?.complaints || listData?.data || [];

  // ── Columns ───────────────────────────────────────────────────────────

  const columns: Column<any>[] = [
    {
      key: 'referenceNumber',
      header: 'Reference #',
      sortable: true,
      sortValue: (r: any) => r.referenceNumber || '',
      render: (row: any) => (
        <Link
          to={`/complaints/${row.id}`}
          className="font-medium text-teal-600 hover:text-teal-700 hover:underline"
        >
          {row.referenceNumber}
        </Link>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      sortValue: (r: any) => r.subject || '',
      render: (row: any) => (
        <span className="max-w-xs truncate text-sm text-gray-900">
          {row.subject}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row: any) => (
        <StatusBadge variant="info">
          {CATEGORY_LABEL[row.category] || row.category}
        </StatusBadge>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      sortValue: (r: any) => {
        const order: Record<string, number> = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
        return order[r.priority] || 0;
      },
      render: (row: any) => (
        <StatusBadge variant={PRIORITY_BADGE_MAP[row.priority] || 'neutral'}>
          {row.priority}
        </StatusBadge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: any) => (
        <StatusBadge variant={STATUS_BADGE_MAP[row.status] || 'neutral'}>
          {STATUS_LABEL[row.status] || row.status}
        </StatusBadge>
      ),
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      render: (row: any) => (
        <span className="text-sm text-gray-600">
          {row.assignedTo || '—'}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      sortable: true,
      sortValue: (r: any) => r.dueDate || '',
      render: (row: any) => (
        <span className="text-sm text-gray-500">
          {row.dueDate
            ? new Date(row.dueDate).toLocaleDateString()
            : '—'}
        </span>
      ),
    },
    {
      key: 'slaStatus',
      header: 'SLA',
      render: (row: any) => {
        if (!row.isDispute && !row.slaStatus) return <span className="text-xs text-gray-300">—</span>;
        const slaMap: Record<string, BadgeVariant> = { ON_TRACK: 'success', AT_RISK: 'warning', OVERDUE: 'danger' };
        const slaLabel: Record<string, string> = { ON_TRACK: 'On Track', AT_RISK: 'At Risk', OVERDUE: 'Overdue' };
        return (
          <StatusBadge variant={slaMap[row.slaStatus] || 'neutral'}>
            {slaLabel[row.slaStatus] || row.slaStatus || '—'}
          </StatusBadge>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      sortValue: (r: any) => r.createdAt || '',
      render: (row: any) => (
        <span className="text-sm text-gray-500">
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString()
            : '—'}
        </span>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <FeatureGate feature="complaint_management">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Complaints & Disputes"
          description="Track, investigate, and resolve customer complaints and disputes with SLA monitoring and regulatory reporting."
          actions={
            <div className="flex items-center gap-2">
              <Link
                to="/complaints/intelligence"
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 transition-colors"
              >
                <ArrowTrendingUpIcon className="h-4 w-4" />
                Intelligence
              </Link>
              <PermissionGate teamId={teamId} permission="compliance.write">
                <>
                  <Link
                    to="/complaints/bulk-import"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    title="Bulk-import complaint history from another vendor"
                  >
                    Bulk import
                  </Link>
                  <button
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-500 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Log Complaint
                  </button>
                  <button
                    onClick={() => setShowCreateDispute(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 transition-colors"
                  >
                    <ShieldExclamationIcon className="h-4 w-4" />
                    New Dispute
                  </button>
                </>
              </PermissionGate>
            </div>
          }
        />

        {/* Stats Row */}
        {dash && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              icon={ChatBubbleLeftRightIcon}
              label="Open"
              value={dash.open ?? dash.openCount ?? 0}
            />
            <StatCard
              icon={MagnifyingGlassIcon}
              label="Under Investigation"
              value={dash.underInvestigation ?? dash.underInvestigationCount ?? 0}
            />
            <StatCard
              icon={ClockIcon}
              label="Pending Response"
              value={dash.pendingResponse ?? dash.pendingResponseCount ?? 0}
            />
            <StatCard
              icon={ExclamationTriangleIcon}
              label="Avg Resolution Days"
              value={dash.avgResolutionDays ?? dash.averageResolutionDays ?? '—'}
            />
          </div>
        )}

        {/* SLA Stats */}
        {sla && (sla.sla?.atRisk > 0 || sla.sla?.overdue > 0 || sla.disputes?.total > 0) && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              icon={ShieldExclamationIcon}
              label="SLA On Track"
              value={sla.sla?.onTrack ?? 0}
            />
            <StatCard
              icon={ClockIcon}
              label="SLA At Risk"
              value={sla.sla?.atRisk ?? 0}
            />
            <StatCard
              icon={ExclamationTriangleIcon}
              label="SLA Overdue"
              value={sla.sla?.overdue ?? 0}
            />
            <StatCard
              icon={ChatBubbleLeftRightIcon}
              label="Disputes"
              value={sla.disputes?.total ?? 0}
            />
          </div>
        )}

        {/* Tab Bar */}
        <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
          <Tab.List className="flex gap-4 border-b border-gray-200">
            {STATUS_TABS.map((tab) => (
              <Tab
                key={tab.key}
                className={({ selected }) =>
                  clsx(
                    'border-b-2 px-1 py-2 text-sm font-medium transition-colors focus:outline-none',
                    selected
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  )
                }
              >
                {tab.label}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels>
            {STATUS_TABS.map((tab) => (
              <Tab.Panel key={tab.key} className="pt-4">
                <DataTable
                  columns={columns}
                  data={complaints}
                  isLoading={isLoading}
                  keyExtractor={(row: any) => row.id}
                  emptyTitle="No complaints found"
                  emptyDescription="No customer complaints have been logged yet."
                  emptyAction={
                    <PermissionGate teamId={teamId} permission="compliance.write">
                      <button
                        onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Log Complaint
                      </button>
                    </PermissionGate>
                  }
                />
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>

        {/* Log Complaint Dialog */}
        <LogComplaintDialog
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          teamId={teamId}
        />

        {/* New Dispute Dialog */}
        <Transition appear show={showCreateDispute} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowCreateDispute(false)}>
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">New Dispute</Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">Create a dispute with auto-calculated SLA deadlines (Reg E).</p>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Customer Name <span className="text-red-500">*</span></label>
                        <input type="text" value={disputeForm.customerName} onChange={(e) => setDisputeForm({ ...disputeForm, customerName: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Subject <span className="text-red-500">*</span></label>
                        <input type="text" value={disputeForm.subject} onChange={(e) => setDisputeForm({ ...disputeForm, subject: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description <span className="text-red-500">*</span></label>
                        <textarea rows={3} value={disputeForm.description} onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Dispute Type</label>
                          <select value={disputeForm.disputeType} onChange={(e) => setDisputeForm({ ...disputeForm, disputeType: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500">
                            <option value="REG_E">Reg E — Electronic Fund Transfer</option>
                            <option value="REG_E_POS">Reg E — POS Transaction</option>
                            <option value="REG_E_FOREIGN">Reg E — Foreign Transaction</option>
                            <option value="REG_E_NEW_ACCOUNT">Reg E — New Account (&lt;30 days)</option>
                            <option value="BILLING">Billing Dispute</option>
                            <option value="UNAUTHORIZED">Unauthorized Transaction</option>
                            <option value="OTHER">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Priority</label>
                          <select value={disputeForm.priority} onChange={(e) => setDisputeForm({ ...disputeForm, priority: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500">
                            <option value="LOW">Low</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HIGH">High</option>
                            <option value="CRITICAL">Critical</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button onClick={() => setShowCreateDispute(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                      <button
                        onClick={() => createDisputeMutation.mutate(disputeForm)}
                        disabled={!disputeForm.customerName.trim() || !disputeForm.subject.trim() || !disputeForm.description.trim() || createDisputeMutation.isPending}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        {createDisputeMutation.isPending ? 'Creating...' : 'Create Dispute'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}

// ─── Log Complaint Dialog ─────────────────────────────────────────────────────

interface LogComplaintDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamId?: string;
}

function LogComplaintDialog({ isOpen, onClose, teamId }: LogComplaintDialogProps) {
  const queryClient = useQueryClient();

  const initialForm = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    category: 'BILLING',
    channel: 'PHONE',
    priority: 'MEDIUM',
    subject: '',
    description: '',
    desiredResolution: '',
    regulatoryBody: '',
    dueDate: '',
  };
  const [form, setForm] = useState(initialForm);
  // Field-level error state — populated from client validation OR backend
  // validation_failed responses (via extractFieldErrors). Keys are field
  // names matching the form state keys. QA #3 finding B: empty form
  // submissions previously generated generic toasts with no inline errors.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear that field's error as soon as the user edits it
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setFieldErrors({});
  };

  const createMutation = useMutation({
    mutationFn: (data: any) => complaintApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      queryClient.invalidateQueries({ queryKey: ['complaintDashboard'] });
      toast.success('Complaint logged successfully');
      resetForm();
      onClose();
    },
    onError: (err: any) => {
      // Backend validation errors → render inline next to the offending
      // field instead of a generic toast. The global axios interceptor
      // already skips toasting validation_failed errors so there's no
      // duplicate notification.
      const serverFieldErrors = extractFieldErrors(err);
      if (Object.keys(serverFieldErrors).length > 0) {
        setFieldErrors(serverFieldErrors);
        return;
      }
      // Non-validation errors fall through to the global toast handler
      // (added in commit 844a206). No need to call toast.error here.
    },
  });

  const handleSubmit = () => {
    // Client-side validation — short-circuits to inline errors before any
    // network request. Mirrors the backend Zod schema for the same fields.
    const errs: Record<string, string> = {};
    if (!form.customerName.trim()) errs.customerName = 'Customer name is required';
    if (!form.subject.trim()) errs.subject = 'Subject is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    if (form.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail)) {
      errs.customerEmail = 'Enter a valid email address';
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setFieldErrors({});
    createMutation.mutate({
      ...form,
      customerEmail: form.customerEmail || undefined,
      customerPhone: form.customerPhone || undefined,
      desiredResolution: form.desiredResolution || undefined,
      regulatoryBody: form.regulatoryBody || undefined,
      dueDate: form.dueDate || undefined,
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="text-lg font-semibold text-navy-900">
                    Log Complaint
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mt-5 space-y-4">
                  {/* Customer Info */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Customer Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.customerName}
                        onChange={(e) => set('customerName', e.target.value)}
                        aria-invalid={!!fieldErrors.customerName}
                        className={clsx(
                          'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                          fieldErrors.customerName
                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500',
                        )}
                      />
                      {fieldErrors.customerName && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors.customerName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Customer Email
                      </label>
                      <input
                        type="email"
                        value={form.customerEmail}
                        onChange={(e) => set('customerEmail', e.target.value)}
                        aria-invalid={!!fieldErrors.customerEmail}
                        className={clsx(
                          'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                          fieldErrors.customerEmail
                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500',
                        )}
                      />
                      {fieldErrors.customerEmail && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors.customerEmail}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Customer Phone
                      </label>
                      <input
                        type="tel"
                        value={form.customerPhone}
                        onChange={(e) => set('customerPhone', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  {/* Category / Channel / Priority */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {/* Category Listbox */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Category
                      </label>
                      <Listbox
                        value={form.category}
                        onChange={(v) => set('category', v)}
                      >
                        <div className="relative mt-1">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-teal-500 focus:ring-teal-500">
                            <span className="block truncate">
                              {CATEGORIES.find((c) => c.value === form.category)
                                ?.label || form.category}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                              {CATEGORIES.map((c) => (
                                <Listbox.Option
                                  key={c.value}
                                  value={c.value}
                                  className={({ active }) =>
                                    clsx(
                                      'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                      active
                                        ? 'bg-teal-50 text-teal-700'
                                        : 'text-gray-900',
                                    )
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={clsx(
                                          'block truncate',
                                          selected && 'font-medium',
                                        )}
                                      >
                                        {c.label}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>

                    {/* Channel Listbox */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Channel
                      </label>
                      <Listbox
                        value={form.channel}
                        onChange={(v) => set('channel', v)}
                      >
                        <div className="relative mt-1">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-teal-500 focus:ring-teal-500">
                            <span className="block truncate">
                              {CHANNELS.find((c) => c.value === form.channel)
                                ?.label || form.channel}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                              {CHANNELS.map((c) => (
                                <Listbox.Option
                                  key={c.value}
                                  value={c.value}
                                  className={({ active }) =>
                                    clsx(
                                      'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                      active
                                        ? 'bg-teal-50 text-teal-700'
                                        : 'text-gray-900',
                                    )
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={clsx(
                                          'block truncate',
                                          selected && 'font-medium',
                                        )}
                                      >
                                        {c.label}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>

                    {/* Priority Listbox */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Priority
                      </label>
                      <Listbox
                        value={form.priority}
                        onChange={(v) => set('priority', v)}
                      >
                        <div className="relative mt-1">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-teal-500 focus:ring-teal-500">
                            <span className="block truncate">
                              {PRIORITIES.find((p) => p.value === form.priority)
                                ?.label || form.priority}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                              {PRIORITIES.map((p) => (
                                <Listbox.Option
                                  key={p.value}
                                  value={p.value}
                                  className={({ active }) =>
                                    clsx(
                                      'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                      active
                                        ? 'bg-teal-50 text-teal-700'
                                        : 'text-gray-900',
                                    )
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={clsx(
                                          'block truncate',
                                          selected && 'font-medium',
                                        )}
                                      >
                                        {p.label}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => set('subject', e.target.value)}
                      aria-invalid={!!fieldErrors.subject}
                      className={clsx(
                        'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                        fieldErrors.subject
                          ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500',
                      )}
                      placeholder="Brief summary of the complaint"
                    />
                    {fieldErrors.subject && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.subject}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) => set('description', e.target.value)}
                      rows={4}
                      aria-invalid={!!fieldErrors.description}
                      className={clsx(
                        'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                        fieldErrors.description
                          ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500',
                      )}
                      placeholder="Detailed description of the complaint..."
                    />
                    {fieldErrors.description && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.description}</p>
                    )}
                  </div>

                  {/* Desired Resolution */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Desired Resolution
                    </label>
                    <textarea
                      value={form.desiredResolution}
                      onChange={(e) => set('desiredResolution', e.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      placeholder="What resolution does the customer seek?"
                    />
                  </div>

                  {/* Regulatory Body + Due Date */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Regulatory Body
                      </label>
                      <input
                        type="text"
                        value={form.regulatoryBody}
                        onChange={(e) => set('regulatoryBody', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        placeholder="e.g., CFPB, OCC"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={form.dueDate}
                        onChange={(e) => set('dueDate', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={createMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                  >
                    {createMutation.isPending ? 'Submitting...' : 'Log Complaint'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
